import { Link } from "react-router-dom";
import FavoriteButton from "@/components/feature/FavoriteButton";
import { getActorsByMovie, formatReleaseDate, getTypeMeta } from "@/utils/movie";

interface Props {
  movie: any;
  onShare: () => void;
}

export default function DetailHero({ movie, onShare }: Props) {
  const cast = getActorsByMovie(movie);

  const meta = [
    { icon: "ri-price-tag-3-line", label: "类型", value: (movie.genres || []).join(" / ") },
    { icon: "ri-calendar-line", label: "上映", value: formatReleaseDate(movie.releaseDate) },
    { icon: "ri-user-star-line", label: "导演", value: movie.director },
    { icon: "ri-earth-line", label: "地区", value: movie.region },
    { icon: "ri-time-line", label: movie.episodes > 1 ? "集数" : "片长", value: movie.duration },
    {
      icon: "ri-group-line",
      label: "主演",
      value: cast.map((a: any) => a.name).join(" / "),
    },
  ];

  return (
    <section className="relative bg-foreground-950 pt-16 md:pt-[72px]">
      <div className="absolute inset-0 overflow-hidden">
        <img src={movie.banner} alt={`${movie.title} 剧照`} className="w-full h-full object-cover object-top opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/85 to-foreground-950/70"></div>
      </div>

      <div className="relative max-w-[1280px] mx-auto px-4 md:px-8 py-8 md:py-12">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs md:text-sm text-background-200/80 mb-6">
          <Link href="/" to="/" className="hover:text-accent-400 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link href="/category" to="/category" className="hover:text-accent-400 transition-colors">影视分类</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-background-100">{movie.title}</span>
        </nav>

        <div className="flex flex-col md:flex-row gap-6 md:gap-9">
          <div className="w-[170px] sm:w-[200px] md:w-[240px] shrink-0 mx-auto md:mx-0">
            <div className="aspect-[2/3] rounded-lg overflow-hidden border border-background-50/15">
              <img
                src={movie.poster}
                alt={`${movie.title}（${movie.year}）影视海报`}
                title={`${movie.title} 剧情介绍与演员表`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50 inline-flex items-center gap-1">
                <i className={getTypeMeta(movie.type)}></i>
                {movie.type}
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-background-50/15 text-background-50 border border-background-50/25">
                {movie.year}
              </span>
              <span className="inline-flex items-center gap-1 text-sm text-accent-400 font-medium">
                <i className="ri-star-fill"></i>
                {movie.rating}
                <span className="text-background-200/70 font-normal text-xs">/ 10</span>
              </span>
            </div>

            <h1 className="font-heading text-3xl md:text-4xl lg:text-[42px] font-bold text-background-50 leading-tight">
              {movie.title}
            </h1>
            <p className="mt-1 text-sm text-background-200/70">{movie.title} ({movie.year}) 剧情介绍与演员资料</p>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5">
              {meta.map((m) => (
                <div key={m.label} className="flex items-start gap-2 text-sm">
                  <i className={`${m.icon} text-accent-400 mt-0.5`}></i>
                  <span className="text-background-200/70 shrink-0">{m.label}：</span>
                  <span className="text-background-100 min-w-0">{m.value}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              {(movie.tags || []).map((t: string) => (
                <span key={t} className="px-2.5 py-1 rounded-full text-xs bg-background-50/10 text-background-100 border border-background-50/15">
                  #{t}
                </span>
              ))}
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <FavoriteButton movieId={movie.id} variant="button" />
              <button
                onClick={onShare}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-background-50/10 text-background-50 border border-background-50/30 text-sm font-medium hover:bg-background-50/20 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-share-forward-line text-base"></i>
                分享
              </button>
              <Link
                href="/category"
                to="/category"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-background-200/90 text-sm font-medium hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回分类
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}